// React and core dependencies
import React, { useState } from 'react';
// Sheet component imports for slide-out mobile menu functionality
import {
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  Sheet,
  SheetDescription,
  SheetClose,
} from '@/components/Sheet/Sheet.component';
// Component interfaces and utilities
import { MobileHeaderProps } from './MobileHeader.interface';
import Button from '@/elements/Button/Button.component';
import { clsx } from 'clsx';
// Accordion component imports for collapsible sub-navigation
import {
  AccordionRoot,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/Accordion/Accordion.component';
// Component styles and shared link components
import './MobileHeader.styles.css';
import { HeaderLevelOneLink, HeaderLevelTwoLink } from '../GlobalHeader/HeaderComponents.component';

// Custom hook to dynamically position mobile menu below the trigger button
const useMobileHeaderTop = () => {
  // Reference to the menu trigger button for position calculations
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  // State to store the calculated top position of the mobile menu
  const [mobileHeaderTop, setMobileHeaderTop] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    // Function to calculate and update the menu's top position
    const updateTop = () => {
      if (typeof window === 'undefined' || !triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      // Position menu 20px below the bottom of the trigger button
      setMobileHeaderTop(`${rect.bottom + 20}px`);
    };

    // Initial position calculation
    updateTop();
    // Update position on window resize to maintain proper positioning
    window.addEventListener('resize', updateTop);
    // Update position on scroll to keep menu aligned with trigger button
    window.addEventListener('scroll', updateTop, { passive: true });

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', updateTop);
    };
  }, []);

  return { triggerRef, mobileHeaderTop };
};

// Main mobile header component that renders a collapsible navigation menu
const MobileHeader = ({ navItems }: MobileHeaderProps) => {
  // Get positioning utilities and menu state management
  const { triggerRef, mobileHeaderTop } = useMobileHeaderTop();
  const [open, setOpen] = useState(false);
  
  return (
    // Main sheet container for the mobile menu
    <Sheet open={open} onOpenChange={setOpen}>
      {/* Hamburger menu trigger button */}
      <SheetTrigger asChild className="mobileHeader__lvl1--button">
        <Button
          ref={triggerRef}
          variant="tertiary"
          color="black"
          size="base"
          icon={open ? 'xmark' : 'bars'} // Toggle between hamburger and close icons
          aria-label={open ? 'Close Mobile Mega Menu' : 'Open Mobile Mega Menu'}
        />
      </SheetTrigger>
      {/* LEVEL 1: Main mobile menu content panel */}
      <SheetContent
        side={'top'} // Slide down from top of screen
        hideClose // Don't show default close button
        className={clsx(
          'mobileHeader__lvl1 z-20 left-0 right-0 w-full bottom-auto',
          // 'py-6 px-2',
          // 'shadow-md',
          'p-4',
          'shadow-none border-none !bg-transparent'
        )}
        style={mobileHeaderTop ? { top: mobileHeaderTop } : undefined} // Dynamic positioning
      >
        {/* Accessibility headers (screen reader only) */}
        <SheetHeader className="sr-only">
          <SheetDescription className="sr-only">
            Mobile mega menu
          </SheetDescription>
          <SheetTitle className="sr-only">Mobile mega menu</SheetTitle>
        </SheetHeader>

        {/* LEVEL 1: Main navigation container */}
        <nav
          aria-label="Main navigation"
          className={clsx(
            'nav flex flex-col gap-2',
            'mobileHeader__popupContainer'
          )}
        >
          <ul className="list-none p-0 m-0">
            {/* LEVEL 1: Iterate through navigation items */}
            {navItems?.map((item, itemindex) => {
              // LEVEL 1: Handle dropdown/mega menu items with nested navigation
              if (item.type === 'dropdown') {
                return (
                  <li
                    className="border-b border-gray-200 last-of-type:border-none py-2 m-0"
                    key={`mobile-header-nav-item-${itemindex}`}
                  >
                    {/* LEVEL 2: Nested sheet for second-level navigation */}
                    <Sheet key={`mobile-header-nav-item-${itemindex}`}>
                      {/* LEVEL 2: Button to trigger second-level menu (matches mobileHeader__lvl2--button) */}
                      <SheetTrigger
                        asChild
                        className="mobileHeader__lvl2--button w-full"
                      >
                        <Button
                          as="button"
                          variant="tertiary"
                          icon={'chevron-right'}
                          iconPosition="after"
                          size="xs"
                        >
                          {item.heading}
                        </Button>
                      </SheetTrigger>
                      {/* LEVEL 2: Second-level menu content */}
                      <SheetContent
                        className={clsx(
                          'mobileHeader__lvl2 z-30 left-0 right-0 w-full bottom-auto min-h-[300px]',
                          // 'py-6 px-0',
                          'p-4',
                          'shadow-none border-none !bg-transparent'
                        )}
                        style={
                          mobileHeaderTop ? { top: mobileHeaderTop } : undefined
                        }
                        hideClose
                      >
                        <div className="mobileHeader__popupContainer py-4">
                          {/* LEVEL 2: Back button header for second-level menu */}
                          <SheetHeader className="pb-6">
                            <SheetClose asChild>
                              <Button
                                as="button"
                                variant="secondary"
                                size="xs"
                                icon="chevron-left"
                                iconPosition="before"
                                className="mobileHeader__lvl2--back-button !text-brand hover:!text-white"
                              >
                                Back
                              </Button>
                            </SheetClose>
                          </SheetHeader>
                          {/* LEVEL 2: Accordion container for collapsible sub-navigation */}
                          <AccordionRoot
                            type="single"
                            collapsible
                            className="accordion"
                          >
                            {/* LEVEL 2: Optional main link for the dropdown section */}
                            {item.link?.href && item.link?.text && (
                              <HeaderLevelOneLink {...item.link} className="!mb-0" />
                            )}
                            {/* LEVEL 2→3: Render each link list as an accordion item */}
                            {item.linkLists?.map((linkList, linkListIndex) => {
                              return (
                                <AccordionItem
                                  className="mobileHeader__lvl3 accordion__item"
                                  value={`linklist-${linkListIndex}`}
                                  key={`mobile-header-nav-item-${itemindex}-linklist-${linkListIndex}`}
                                >
                                  {/* LEVEL 3: Collapsible section header (matches mobileHeader__lvl3) */}
                                  <AccordionTrigger>
                                    {linkList.heading?.text}
                                  </AccordionTrigger>
                                  {/* LEVEL 3: Collapsible section content with links */}
                                  <AccordionContent>
                                      <div className="flex flex-col gap-2 justify-content-start items-start pb-6">
                                        {/* LEVEL 3: Optional section heading link */}
                                        {linkList.heading?.href && linkList.heading?.text && (
                                          <HeaderLevelTwoLink {...linkList.heading} />
                                        )}
                                      {/* LEVEL 3→4: Render sub-links for this section */}
                                      {linkList?.subLinks?.map(
                                        (subLink, subLinkIndex) => {
                                          return (
                                            <div key={`mobile-header-nav-item-${itemindex}-linklist-${linkListIndex}-sublink-${subLinkIndex}`}>
                                              {/* LEVEL 3: Third-level navigation link (matches mobileHeader__lvl3--link) */}
                                              <a
                                                className="mobileHeader__lvl3--link"
                                                href={subLink?.href}
                                              >
                                                {subLink?.text}
                                              </a>
                                              {/* LEVEL 4: Fourth-level nested links (deepest navigation level) */}
                                              {subLink?.subLinks && subLink.subLinks.length > 0 && (
                                                <div className="mobileHeader__lvl4 pl-4 mt-2">
                                                  {subLink.subLinks.map((subSubLink, subSubLinkIndex) => (
                                                    <a
                                                      key={`mobile-header-nav-item-${itemindex}-linklist-${linkListIndex}-sublink-${subLinkIndex}-subsublink-${subSubLinkIndex}`}
                                                      className="mobileHeader__lvl4--link"
                                                      href={subSubLink?.href}
                                                    >
                                                      {subSubLink?.text}
                                                    </a>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </AccordionRoot>

                          {/* LEVEL 2: Optional promotional content section */}
                          {item?.promo?.button && (
                            <div className="mobileHeader__promo">
                              <Button
                                {...item.promo.button}
                                children={item.promo.heading || item.promo.button.children}
                                className="mobileHeader__promoButton w-full"
                              />
                            </div>
                          )}
                        </div>
                      </SheetContent>
                    </Sheet>
                  </li>
                );
              } else {
                // LEVEL 1: Handle simple navigation items (non-dropdown)
                return (
                  <li
                    className="border-b border-gray-200 py-3"
                    key={`mobile-header-nav-item-${itemindex}`}
                  >
                    {/* LEVEL 2: Simple navigation link button (matches mobileHeader__lvl2--button) */}
                    <Button
                      key={`mobile-header-nav-item-${itemindex}`}
                      className="mobileHeader__lvl2--button w-full"
                      as="a"
                      href={item.link.href}
                      children={item.link.text}
                      variant="tertiary"
                      icon="chevron-right"
                      iconPosition="after"
                    />
                  </li>
                );
              }
            })}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default MobileHeader;
